import { useState, type ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { ROLE_LABEL, Shell } from "@/app/Shell";
import { useMe } from "@/app/session";
import { RequestError } from "@/app/queries";
import { submissionExportUrl, useAssignment } from "@/app/notebookQueries";
import { useAddComment, useReasons, useSaveChecklist, useSubmission, useVerdict, waitLabel, type CommentInput, type SubmissionView } from "@/app/reviewQueries";
import type { Task } from "@/kernel/runner";
import { StatusChip } from "@/ui/StatusChip";
import { NoAccess, NotFound, SkeletonRows, StateCard } from "@/ui/states";
import { Checklist, HistoryCard, Reasons, RunCard, VerdictCard, VerdictDone } from "./mentor/review/Aside";
import { CellNav, Cells } from "./mentor/review/Cells";
import { flagText } from "./mentor/review/model";
import { cellRows, fileName, runScore, SLA_H, STATUS_CHIP, waitHours } from "./submission/model";
import { RoleAvatar } from "./submission/ThreadView";

type User = { name: string; role: string } | undefined;

/** M2 «Ревью ноутбука» глазами ментора: ячейки версии с выводами прогона,
 *  треды и «+ комментарий», чек-лист, вердикт по правилу §2.1. */
export function ReviewPage({ submissionId }: { submissionId: string }) {
  const me = useMe();
  const user: User = me.data ? { name: me.data.name, role: ROLE_LABEL[me.data.role] ?? me.data.role } : undefined;
  const sub = useSubmission(submissionId);
  const frame = (children: ReactNode) => (
    <Shell nav user={user}>
      {children}
    </Shell>
  );
  const err = sub.error;
  if (err instanceof RequestError && err.status === 404) return frame(<NotFound primary={{ label: "в очередь", href: "/mentor/queue" }} />);
  if (err instanceof RequestError && err.status === 403) return frame(<NoAccess where="ревью" body="Сдачи проверяют менторы и админы школы. Студент видит свою сдачу в разделе домашек." primary={{ label: "на мой маршрут", href: "/" }} />);
  if (err)
    return frame(
      <StateCard code="ERR_NETWORK" where="ревью" tone="warn" title="Нет связи с сервером" body="Сдача не загрузилась. Комментарии и вердикты хранятся на сервере — ничего не потеряно." primary={{ label: "повторить сейчас", onClick: () => void sub.refetch() }} />,
    );
  if (!sub.data) return frame(<SkeletonRows />);
  return <Review view={sub.data} me={me.data?.name ?? "ментор"} user={user} />;
}

function Review({ view, me, user }: { view: SubmissionView; me: string; user: User }) {
  // Задание — только для названий задач; ментору шаг может быть «закрыт» — тогда без названий.
  const asg = useAssignment(view.assignment_id);
  const tasks = (asg.data?.manifest.tasks ?? []) as Task[];
  const reasons = useReasons();
  const addComment = useAddComment(view.id);
  const verdict = useVerdict(view.id);
  const saveChecklist = useSaveChecklist(view.id);

  const [composing, setComposing] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [checklist, setChecklist] = useState<Record<string, boolean>>(view.checklist);
  const [verdictError, setVerdictError] = useState<string | null>(null);

  const rows = cellRows(view, tasks);
  const onReview = view.status === "on_review";
  const score = runScore(view);
  const hours = onReview ? waitHours(view.submitted_at) : view.verdict_at ? waitHours(view.submitted_at, Date.parse(view.verdict_at)) : 0;

  const submitComment = async (input: CommentInput) => {
    await addComment.mutateAsync(input);
    setDrafts((d) => ({ ...d, [input.cell_id ?? ""]: "" }));
    setComposing(null);
  };
  const reply = async (parentId: string, cellId: string, taskN: number | undefined, body: string) => {
    await addComment.mutateAsync({ parent_id: parentId, cell_id: cellId, task_n: taskN, body });
  };
  const send = async (status: "accepted" | "changes_requested", summary: string) => {
    setVerdictError(null);
    try {
      await verdict.mutateAsync({ status, summary });
    } catch (e) {
      setVerdictError(e instanceof Error && e.message ? e.message : "Вердикт не отправился — попробуй ещё раз, комментарии сохранены.");
    }
  };
  const changeChecklist = (items: Record<string, boolean>) => {
    setChecklist(items);
    saveChecklist.mutate(items);
  };

  return (
    <Shell wide user={user}>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-2.5 font-mono text-[12px] text-mut">
        <Link to="/mentor/queue" className="text-mut no-underline hover:text-ink">
          очередь
        </Link>
        <span>/</span>
        <span>ревью</span>
      </div>
      <div className="flex flex-wrap items-center gap-3 border-b border-bd px-5 py-3" data-testid="review-head">
        <RoleAvatar name={view.student.name} student={false} />
        <Link to="/mentor/student/$userId" params={{ userId: view.student.id }} className="text-[14px] font-semibold text-ink no-underline hover:underline">
          {view.student.name}
        </Link>
        <span className="font-mono text-[12px] text-ink2">{fileName(view.assignment)}</span>
        <span className="font-mono text-[12px] text-ink">v{view.iteration}</span>
        <StatusChip status={STATUS_CHIP[view.status]} compact />
        <span
          data-testid="wait"
          className="rounded-[3px] border px-2 py-[3px] font-mono text-[11px]"
          style={onReview && hours >= SLA_H ? { background: "var(--errsoft)", color: "var(--err)", borderColor: "var(--err)" } : { background: "transparent", color: "var(--ink2)", borderColor: "var(--bd2)" }}
        >
          {onReview ? `ждёт ${waitLabel(hours)}` : `ответ через ${waitLabel(hours)}`}
        </span>
        <a href={submissionExportUrl(view.id)} download className="font-mono text-[11px] text-acc no-underline underline-offset-2 hover:underline" data-testid="download-version">
          скачать v{view.iteration} .ipynb
        </a>
        <span className="flex-1" />
        <span className="font-mono text-[12px]" data-testid="run-score" style={{ color: score && score.passed === score.total ? "var(--ok)" : score ? "var(--err)" : "var(--warn)" }}>
          {score ? `${score.passed} / ${score.total} автопроверок` : "без автопроверок"}
        </span>
      </div>
      {view.flag && (
        <div data-testid="flag" role="status" className="mx-5 mt-3 flex items-center gap-2.5 rounded-md border border-warn bg-card px-3.5 py-2.5 text-[12.5px] leading-[1.5] text-ink" style={{ borderLeft: "2px solid var(--warn)" }}>
          <span className="font-mono text-[11px]" style={{ color: "var(--warn)" }}>
            созвон
          </span>
          <span className="text-pretty">{flagText(view.flag.count, view.flag.reason.label)}</span>
          <span className="flex-1" />
          <Link to="/mentor/student/$userId" params={{ userId: view.student.id }} className="font-mono text-[11.5px] text-acc underline-offset-2 hover:underline">
            карточка студента →
          </Link>
        </div>
      )}
      <div className="grid grid-cols-1 lg:grid-cols-[208px_minmax(0,1fr)_380px]">
        <aside className="border-bd px-3.5 py-[18px] lg:border-r">
          <CellNav rows={rows} />
        </aside>
        <div className="px-5 pb-10 pt-[18px]">
          {rows.length === 0 ? (
            <p className="m-0 text-[13px] leading-[1.55] text-ink2">У этой версии нет результата прогона и задач — комментировать нечего. Скачай ноутбук версии, чтобы посмотреть код; прими работу или попроси студента прислать новую версию.</p>
          ) : (
            <Cells
              rows={rows}
              view={view}
              composing={composing}
              drafts={drafts}
              reasons={reasons.data ?? []}
              me={me}
              canComment={onReview}
              onCompose={setComposing}
              onDraft={(id, t) => setDrafts((d) => ({ ...d, [id]: t }))}
              onCancel={() => {
                if (composing) setDrafts((d) => ({ ...d, [composing]: "" }));
                setComposing(null);
              }}
              onSubmit={submitComment}
              onReply={reply}
            />
          )}
        </div>
        <aside className="flex flex-col gap-3.5 border-bd px-4 py-[18px] lg:border-l">
          <RunCard view={view} />
          {onReview && <Checklist items={checklist} onChange={changeChecklist} />}
          <Reasons view={view} />
          {onReview ? <VerdictCard view={view} onSend={send} busy={verdict.isPending} error={verdictError} /> : <VerdictDone view={view} />}
          <HistoryCard history={view.history} />
        </aside>
      </div>
    </Shell>
  );
}
